import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Star } from 'lucide-react';
import useApi from '../api';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';

// ---------------------------------------------------------------------------
// Types (mirror backend/api/routes/metering_admin.py — credit plans section)
// ---------------------------------------------------------------------------

export type CreditPlanKind = 'subscription' | 'pack';

export interface CreditPlan {
    id: string;
    kind: CreditPlanKind;
    name: string;
    group_label: string | null;
    credits: number;
    price_cents: number;
    currency: string;
    interval: string | null;
    bonus_pct: number;
    is_featured: boolean;
    sort_order: number;
    lookup_key: string;
    is_active: boolean;
    stripe_product_id: string | null;
    stripe_price_id: string | null;
    created_at: string | null;
    updated_at: string | null;
    archived_at: string | null;
    subscriber_count: number | null;
}

type SyncActionKind =
    | 'noop'
    | 'create_product_and_price'
    | 'create_price'
    | 'replace_price'
    | 'rename_product'
    | 'archive_price';

interface SyncAction {
    plan_id: string;
    lookup_key: string;
    name: string;
    action: SyncActionKind;
    reason: string | null;
}

interface SyncResult extends SyncAction {
    status?: 'ok' | 'error';
    error?: string;
    price_id?: string;
    archived_price_id?: string;
}

interface PlanFormState {
    id: string | null;
    kind: CreditPlanKind;
    name: string;
    nameTouched: boolean;
    group_label: string;
    price_dollars: string;
    credits: string;
    is_featured: boolean;
    sort_order: string;
}

const EMPTY_FORM: PlanFormState = {
    id: null,
    kind: 'subscription',
    name: '',
    nameTouched: false,
    group_label: '',
    price_dollars: '',
    credits: '',
    is_featured: false,
    sort_order: '0',
};

const SYNC_ACTION_LABELS: Record<SyncActionKind, string> = {
    noop: 'Up to date',
    create_product_and_price: 'Create product + price',
    create_price: 'Create price',
    replace_price: 'Replace price (archive old)',
    rename_product: 'Rename product',
    archive_price: 'Archive price',
};

const fmtUsd = (cents: number) =>
    (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
    });

const fmtInt = (value: number) => value.toLocaleString(undefined, { maximumFractionDigits: 0 });

const fmtBonus = (pct: number) => (pct > 0 ? `+${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%` : '—');

const suggestedName = (credits: number, kind: CreditPlanKind) =>
    `${fmtInt(credits)} credits${kind === 'subscription' ? ' / month' : ' pack'}`;

const shortId = (value: string | null) => (value ? `${value.slice(0, 12)}…` : '');

const errorDetail = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object' && 'detail' in error) {
        const detail = (error as { detail?: unknown }).detail;
        if (typeof detail === 'string') return detail;
    }
    return fallback;
};

const formFromPlan = (plan: CreditPlan): PlanFormState => ({
    id: plan.id,
    kind: plan.kind,
    name: plan.name,
    nameTouched: true,
    group_label: plan.group_label || '',
    price_dollars: String(plan.price_cents / 100),
    credits: String(plan.credits),
    is_featured: plan.is_featured,
    sort_order: String(plan.sort_order),
});

// ---------------------------------------------------------------------------

const CreditPlansSection: React.FC = () => {
    const { get, post, put } = useApi();

    const [plans, setPlans] = useState<CreditPlan[]>([]);
    const [stripeMode, setStripeMode] = useState<'live' | 'test' | null>(null);
    const [loading, setLoading] = useState(true);
    const [showArchived, setShowArchived] = useState(false);

    const [form, setForm] = useState<PlanFormState | null>(null);
    const [saving, setSaving] = useState(false);
    const [togglingId, setTogglingId] = useState<string | null>(null);

    const [syncOpen, setSyncOpen] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);
    const [syncActions, setSyncActions] = useState<SyncAction[] | null>(null);
    const [syncResults, setSyncResults] = useState<SyncResult[] | null>(null);
    const [applying, setApplying] = useState(false);

    const loadPlans = useCallback(async () => {
        try {
            const response = await get('/admin/metering/plans');
            setPlans(Array.isArray(response.data?.plans) ? response.data.plans : []);
            setStripeMode(response.data?.stripe_mode === 'live' ? 'live' : 'test');
        } catch (error) {
            console.error('Error loading credit plans:', error);
            toast.error('Failed to load credit plans');
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    const sortedPlans = useMemo(() => {
        const kindRank = (kind: CreditPlanKind) => (kind === 'subscription' ? 0 : 1);
        return [...plans].sort((a, b) => {
            if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
            if (a.kind !== b.kind) return kindRank(a.kind) - kindRank(b.kind);
            if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
            return a.price_cents - b.price_cents;
        });
    }, [plans]);

    const visiblePlans = useMemo(
        () => (showArchived ? sortedPlans : sortedPlans.filter((plan) => plan.is_active)),
        [sortedPlans, showArchived]
    );
    const archivedCount = useMemo(() => plans.filter((plan) => !plan.is_active).length, [plans]);
    const unsyncedCount = useMemo(
        () => plans.filter((plan) => plan.is_active && !plan.stripe_price_id).length,
        [plans]
    );

    // ---- form -------------------------------------------------------------

    const formCents = form ? Math.round(Number.parseFloat(form.price_dollars || '0') * 100) : 0;
    const formCredits = form ? Number.parseInt(form.credits || '0', 10) : 0;
    const formBonus =
        formCents > 0 && Number.isFinite(formCredits) ? (formCredits / formCents - 1) * 100 : null;

    const updateForm = (patch: Partial<PlanFormState>) => {
        setForm((current) => {
            if (!current) return current;
            const next = { ...current, ...patch };
            if (!next.nameTouched) {
                const credits = Number.parseInt(next.credits || '0', 10);
                next.name = credits > 0 ? suggestedName(credits, next.kind) : '';
            }
            return next;
        });
    };

    const savePlan = async () => {
        if (!form) return;
        if (!Number.isFinite(formCents) || formCents <= 0) {
            toast.error('Enter a price in dollars');
            return;
        }
        if (!Number.isFinite(formCredits) || formCredits <= 0) {
            toast.error('Enter a credit amount');
            return;
        }
        if (!form.name.trim()) {
            toast.error('Enter a name');
            return;
        }
        const sortOrder = Number.parseInt(form.sort_order || '0', 10);
        setSaving(true);
        try {
            if (form.id) {
                await put(`/admin/metering/plans/${form.id}`, {
                    name: form.name.trim(),
                    group_label: form.group_label.trim(),
                    credits: formCredits,
                    price_cents: formCents,
                    is_featured: form.is_featured,
                    sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
                });
                toast.success('Plan updated — run Sync to push the change to Stripe');
            } else {
                await post('/admin/metering/plans', {
                    kind: form.kind,
                    name: form.name.trim(),
                    group_label: form.group_label.trim() || null,
                    credits: formCredits,
                    price_cents: formCents,
                    is_featured: form.is_featured,
                    sort_order: Number.isFinite(sortOrder) ? sortOrder : 0,
                });
                toast.success('Plan created — run Sync to create it in Stripe');
            }
            setForm(null);
            loadPlans();
        } catch (error) {
            console.error('Error saving credit plan:', error);
            toast.error(errorDetail(error, 'Failed to save plan'));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (plan: CreditPlan) => {
        setTogglingId(plan.id);
        try {
            await put(`/admin/metering/plans/${plan.id}`, { is_active: !plan.is_active });
            toast.success(
                plan.is_active
                    ? 'Plan archived — Sync will archive the Stripe price'
                    : 'Plan restored — Sync will recreate the Stripe price'
            );
            loadPlans();
        } catch (error) {
            console.error('Error toggling credit plan:', error);
            toast.error(errorDetail(error, 'Failed to update plan'));
        } finally {
            setTogglingId(null);
        }
    };

    // ---- sync -------------------------------------------------------------

    const previewSync = async () => {
        setSyncOpen(true);
        setSyncLoading(true);
        setSyncActions(null);
        setSyncResults(null);
        try {
            const response = await get('/admin/metering/plans/sync');
            setSyncActions(Array.isArray(response.data?.actions) ? response.data.actions : []);
        } catch (error) {
            console.error('Error previewing plan sync:', error);
            toast.error(errorDetail(error, 'Failed to preview Stripe sync'));
            setSyncOpen(false);
        } finally {
            setSyncLoading(false);
        }
    };

    const applySync = async () => {
        setApplying(true);
        try {
            const response = await post('/admin/metering/plans/sync', {});
            const results: SyncResult[] = Array.isArray(response.data?.results) ? response.data.results : [];
            setSyncResults(results);
            const failed = Number(response.data?.failed || 0);
            if (failed > 0) {
                toast.error(`Sync finished with ${failed} error${failed === 1 ? '' : 's'}`);
            } else {
                toast.success(`Synced ${Number(response.data?.applied || 0)} change(s) to Stripe`);
            }
            loadPlans();
        } catch (error) {
            console.error('Error applying plan sync:', error);
            toast.error(errorDetail(error, 'Stripe sync failed'));
        } finally {
            setApplying(false);
        }
    };

    const pendingActions = (syncActions || []).filter((action) => action.action !== 'noop');

    // ---- render -----------------------------------------------------------

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-medium">Credit plans</CardTitle>
                    {stripeMode && (
                        <Badge variant={stripeMode === 'live' ? 'default' : 'outline'}>
                            Stripe: {stripeMode}
                        </Badge>
                    )}
                    {unsyncedCount > 0 && (
                        <Badge variant="secondary">
                            {unsyncedCount} not synced
                        </Badge>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {archivedCount > 0 && (
                        <Button size="sm" variant="ghost" onClick={() => setShowArchived((value) => !value)}>
                            {showArchived ? 'Hide archived' : `Show archived (${archivedCount})`}
                        </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={previewSync} disabled={loading}>
                        Preview sync
                    </Button>
                    <Button size="sm" onClick={() => setForm({ ...EMPTY_FORM })}>
                        Add plan
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <p className="mb-3 text-xs text-muted-foreground">
                    The ladder on the pricing page. Rows are the source of truth; Sync mirrors them into
                    Stripe. Editing price or credits on a synced row creates a new Stripe price and
                    archives the old one — existing subscribers stay on the old price.
                </p>
                {loading ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                ) : visiblePlans.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No plans yet.</p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Kind</TableHead>
                                <TableHead>Group</TableHead>
                                <TableHead className="text-right">Price</TableHead>
                                <TableHead className="text-right">Credits</TableHead>
                                <TableHead className="text-right">Bonus</TableHead>
                                <TableHead>Stripe</TableHead>
                                <TableHead className="text-right">Subscribers</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visiblePlans.map((plan) => (
                                <TableRow key={plan.id} className={plan.is_active ? undefined : 'opacity-50'}>
                                    <TableCell>
                                        <span className="inline-flex items-center gap-1.5">
                                            {plan.is_featured && (
                                                <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                                            )}
                                            <span className="font-medium">{plan.name}</span>
                                            {!plan.is_active && (
                                                <Badge variant="outline" className="ml-1">
                                                    Archived
                                                </Badge>
                                            )}
                                        </span>
                                        <div className="text-[11px] text-muted-foreground">{plan.lookup_key}</div>
                                    </TableCell>
                                    <TableCell className="capitalize">{plan.kind}</TableCell>
                                    <TableCell className="text-muted-foreground">{plan.group_label || '—'}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {fmtUsd(plan.price_cents)}
                                        {plan.interval ? (
                                            <span className="text-muted-foreground">/{plan.interval === 'month' ? 'mo' : plan.interval}</span>
                                        ) : null}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">{fmtInt(plan.credits)}</TableCell>
                                    <TableCell className="text-right tabular-nums">{fmtBonus(plan.bonus_pct)}</TableCell>
                                    <TableCell>
                                        {plan.stripe_price_id ? (
                                            <span className="text-xs text-muted-foreground" title={plan.stripe_price_id}>
                                                ✓ {shortId(plan.stripe_price_id)}
                                            </span>
                                        ) : (
                                            <Badge variant="secondary">not synced</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {fmtInt(plan.subscriber_count || 0)}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div className="flex justify-end gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => setForm(formFromPlan(plan))}>
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                disabled={togglingId === plan.id}
                                                onClick={() => toggleActive(plan)}
                                            >
                                                {plan.is_active ? 'Archive' : 'Restore'}
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            {/* Add / edit dialog */}
            <Dialog open={form !== null} onOpenChange={(open) => !open && setForm(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{form?.id ? 'Edit plan' : 'Add plan'}</DialogTitle>
                        <DialogDescription>
                            $1 = 100 credits at the base rung. Volume discounts are bonus credits, never a
                            lower rate (max +25%).
                        </DialogDescription>
                    </DialogHeader>
                    {form && (
                        <div className="grid gap-4">
                            {!form.id && (
                                <div className="grid gap-2">
                                    <Label>Kind</Label>
                                    <Select
                                        value={form.kind}
                                        onValueChange={(value) => updateForm({ kind: value as CreditPlanKind })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="subscription">Monthly subscription</SelectItem>
                                            <SelectItem value="pack">One-time pack</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="plan-price">Price (USD)</Label>
                                    <Input
                                        id="plan-price"
                                        type="number"
                                        min={1}
                                        step={1}
                                        inputMode="decimal"
                                        value={form.price_dollars}
                                        onChange={(event) => updateForm({ price_dollars: event.target.value })}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="plan-credits">Credits</Label>
                                    <Input
                                        id="plan-credits"
                                        type="number"
                                        min={1}
                                        step={100}
                                        inputMode="numeric"
                                        value={form.credits}
                                        onChange={(event) => updateForm({ credits: event.target.value })}
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Bonus over the peg:{' '}
                                <span
                                    className={
                                        formBonus !== null && (formBonus < 0 || formBonus > 25)
                                            ? 'font-medium text-destructive'
                                            : 'font-medium'
                                    }
                                >
                                    {formBonus === null ? '—' : `${formBonus >= 0 ? '+' : ''}${formBonus.toFixed(1)}%`}
                                </span>
                                {formBonus !== null && formBonus < 0 && ' (below the peg — rejected)'}
                                {formBonus !== null && formBonus > 25 && ' (breaches the margin floor — rejected)'}
                            </p>
                            <div className="grid gap-2">
                                <Label htmlFor="plan-name">Name</Label>
                                <Input
                                    id="plan-name"
                                    value={form.name}
                                    placeholder={formCredits > 0 ? suggestedName(formCredits, form.kind) : 'e.g. 52,500 credits / month'}
                                    onChange={(event) => updateForm({ name: event.target.value, nameTouched: true })}
                                />
                                <p className="text-[11px] text-muted-foreground">
                                    Becomes the Stripe product name — it is what shows on invoices.
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="plan-group">Group label</Label>
                                    <Input
                                        id="plan-group"
                                        value={form.group_label}
                                        placeholder="Getting started"
                                        onChange={(event) => updateForm({ group_label: event.target.value })}
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="plan-sort">Sort order</Label>
                                    <Input
                                        id="plan-sort"
                                        type="number"
                                        inputMode="numeric"
                                        value={form.sort_order}
                                        onChange={(event) => updateForm({ sort_order: event.target.value })}
                                    />
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    checked={form.is_featured}
                                    onCheckedChange={(checked) => updateForm({ is_featured: checked === true })}
                                />
                                Featured (&ldquo;Most popular&rdquo; on the pricing page)
                            </label>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setForm(null)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button onClick={savePlan} disabled={saving}>
                            {saving ? 'Saving…' : form?.id ? 'Save changes' : 'Create plan'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sync dialog */}
            <Dialog
                open={syncOpen}
                onOpenChange={(open) => {
                    if (!open && !applying) {
                        setSyncOpen(false);
                    }
                }}
            >
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{syncResults ? 'Sync results' : 'Sync to Stripe — preview'}</DialogTitle>
                        <DialogDescription>
                            {syncResults
                                ? 'What Stripe now holds for each row.'
                                : syncLoading
                                    ? 'Comparing rows against Stripe…'
                                    : pendingActions.length === 0
                                        ? 'Everything is up to date. Nothing will be written.'
                                        : `${pendingActions.length} change${pendingActions.length === 1 ? '' : 's'} will be written to Stripe (${stripeMode ?? 'test'} mode).`}
                        </DialogDescription>
                    </DialogHeader>
                    {!syncLoading && (
                        <div className="max-h-96 overflow-y-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Plan</TableHead>
                                        <TableHead>Action</TableHead>
                                        <TableHead>{syncResults ? 'Result' : 'Reason'}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(syncResults ?? syncActions ?? []).map((row) => {
                                        const result = row as SyncResult;
                                        const isNoop = row.action === 'noop';
                                        return (
                                            <TableRow key={row.plan_id} className={isNoop ? 'opacity-50' : undefined}>
                                                <TableCell>
                                                    <div className="font-medium">{row.name}</div>
                                                    <div className="text-[11px] text-muted-foreground">{row.lookup_key}</div>
                                                </TableCell>
                                                <TableCell>{SYNC_ACTION_LABELS[row.action] || row.action}</TableCell>
                                                <TableCell className="text-xs text-muted-foreground">
                                                    {syncResults ? (
                                                        isNoop ? (
                                                            '—'
                                                        ) : result.status === 'error' ? (
                                                            <span className="text-destructive">{result.error || 'Error'}</span>
                                                        ) : (
                                                            <span>
                                                                ok
                                                                {result.price_id ? ` · ${shortId(result.price_id)}` : ''}
                                                                {result.archived_price_id
                                                                    ? ` · archived ${shortId(result.archived_price_id)}`
                                                                    : ''}
                                                            </span>
                                                        )
                                                    ) : (
                                                        row.reason || '—'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSyncOpen(false)} disabled={applying}>
                            {syncResults ? 'Close' : 'Cancel'}
                        </Button>
                        {!syncResults && (
                            <Button onClick={applySync} disabled={applying || syncLoading || pendingActions.length === 0}>
                                {applying ? 'Applying…' : `Apply sync (${pendingActions.length})`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
};

export default CreditPlansSection;
